<template>
  <div
    v-if="visible"
    class="firma-modal-backdrop"
    @click.self="cerrar"
  >
    <div class="firma-modal" role="dialog" aria-labelledby="firma-doc-titulo">
      <div class="firma-modal__header">
        <h6 id="firma-doc-titulo" class="font-weight-bold mb-0">
          {{ modoSeleccion ? "Seleccionar firma" : "Firmas de documentos" }}
        </h6>
        <button type="button" class="btn btn-sm btn-light border" @click="cerrar">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="firma-modal__body">
        <!-- Lista de firmas guardadas -->
        <div v-if="vista === 'lista'">
          <p class="small text-muted mb-3">
            Puede reutilizar una firma guardada o crear una nueva (dibujar, subir foto o extraer el trazo).
          </p>

          <div v-if="cargandoLista" class="text-center text-muted py-4">
            <span class="spinner-border spinner-border-sm mr-2" />
            Cargando firmas…
          </div>

          <div v-else-if="!firmas.length" class="firma-empty mb-3">
            <i class="fas fa-signature fa-2x text-muted mb-2 d-block"></i>
            <span class="small text-muted">Aún no tiene firmas guardadas.</span>
          </div>

          <div v-else class="firma-grid mb-3">
            <button
              v-for="firma in firmas"
              :key="firma.id"
              type="button"
              class="firma-card"
              :class="{ 'firma-card--activa': firma.activa, 'firma-card--seleccionada': seleccionId === firma.id }"
              @click="onClickFirma(firma)"
            >
              <img :src="firma.url" :alt="firma.alias || 'Firma'" class="firma-card__img" />
              <div class="firma-card__meta">
                <span class="firma-card__alias">{{ firma.alias || "Sin nombre" }}</span>
                <span v-if="firma.activa" class="badge badge-success">Activa</span>
              </div>
              <div class="firma-card__actions" @click.stop>
                <button
                  v-if="!firma.activa"
                  type="button"
                  class="btn btn-xs btn-outline-primary"
                  title="Marcar como activa"
                  @click="activarFirma(firma)"
                >
                  Usar
                </button>
                <button
                  type="button"
                  class="btn btn-xs btn-outline-danger"
                  title="Eliminar"
                  @click="eliminarFirma(firma)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </button>
          </div>

          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <button type="button" class="btn btn-outline-primary btn-sm" @click="abrirNueva">
              <i class="fas fa-plus mr-1"></i>
              Nueva firma
            </button>
            <div v-if="modoSeleccion">
              <button type="button" class="btn btn-light border btn-sm mr-2" @click="cerrar">
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="!firmaSeleccionada"
                @click="confirmarSeleccion"
              >
                Usar esta firma
              </button>
            </div>
          </div>
        </div>

        <!-- Captura nueva -->
        <div v-else>
          <ul class="nav nav-pills firma-tabs mb-3">
            <li class="nav-item">
              <a
                href="#"
                class="nav-link"
                :class="{ active: tab === 'dibujo' }"
                @click.prevent="cambiarTab('dibujo')"
              >Dibujar</a>
            </li>
            <li class="nav-item">
              <a
                href="#"
                class="nav-link"
                :class="{ active: tab === 'foto' }"
                @click.prevent="cambiarTab('foto')"
              >Subir foto</a>
            </li>
            <li class="nav-item">
              <a
                href="#"
                class="nav-link"
                :class="{ active: tab === 'extraida' }"
                @click.prevent="cambiarTab('extraida')"
              >Extraer firma</a>
            </li>
          </ul>

          <div class="form-group">
            <label for="firma-alias">Nombre (opcional)</label>
            <input
              id="firma-alias"
              v-model.trim="alias"
              type="text"
              class="form-control form-control-sm"
              maxlength="100"
              placeholder="Ej. Firma principal"
            />
          </div>

          <!-- Dibujar -->
          <div v-show="tab === 'dibujo'" class="firma-pad-wrap">
            <canvas
              ref="canvasDibujo"
              class="firma-pad"
              width="560"
              height="200"
              @mousedown="startDraw"
              @mousemove="draw"
              @mouseup="endDraw"
              @mouseleave="endDraw"
              @touchstart.prevent="startDrawTouch"
              @touchmove.prevent="drawTouch"
              @touchend.prevent="endDraw"
            />
            <button type="button" class="btn btn-sm btn-light border mt-2" @click="limpiarDibujo">
              Limpiar
            </button>
          </div>

          <!-- Subir foto -->
          <div v-show="tab === 'foto'">
            <input
              ref="inputFoto"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              class="form-control-file"
              @change="onFoto"
            />
            <div v-if="previewFoto" class="firma-preview mt-3">
              <img :src="previewFoto" alt="Vista previa" />
            </div>
          </div>

          <!-- Extraer -->
          <div v-show="tab === 'extraida'">
            <p class="small text-muted mb-2">
              Suba una foto con la firma sobre fondo claro. Se quitará el fondo blanco y se recortará el trazo.
            </p>
            <input
              ref="inputExtraer"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              class="form-control-file"
              @change="onExtraer"
            />
            <div class="form-group mt-2 mb-0">
              <label for="umbral-extraer" class="small mb-1">
                Sensibilidad del fondo: {{ umbral }}
              </label>
              <input
                id="umbral-extraer"
                v-model.number="umbral"
                type="range"
                min="180"
                max="250"
                class="custom-range"
                @input="reprocesarExtraccion"
              />
            </div>
            <div v-if="previewExtraida" class="firma-preview firma-preview--checkered mt-3">
              <img :src="previewExtraida" alt="Firma extraída" />
            </div>
          </div>

          <div class="d-flex justify-content-end flex-wrap mt-4">
            <button type="button" class="btn btn-light border mr-2" @click="volverLista">
              Volver
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="guardando"
              @click="guardarNueva"
            >
              <span v-if="guardando" class="spinner-border spinner-border-sm mr-2" role="status" />
              Guardar firma
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "FirmaDocumentoModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** Si true, permite elegir una firma y emitir selected */
    modoSeleccion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      vista: "lista",
      tab: "dibujo",
      firmas: [],
      cargandoLista: false,
      guardando: false,
      alias: "",
      seleccionId: null,
      previewFoto: null,
      archivoFoto: null,
      previewExtraida: null,
      imagenExtraerOriginal: null,
      umbral: 230,
      dibujando: false,
      tieneTrazo: false,
    };
  },
  computed: {
    firmaSeleccionada() {
      return this.firmas.find((f) => f.id === this.seleccionId) || null;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.vista = "lista";
        this.seleccionId = null;
        this.cargarFirmas();
      }
    },
  },
  methods: {
    mensajeError(e) {
      const d = e && e.response && e.response.data;
      if (!d) return "No se pudo completar la operación.";
      if (typeof d.message === "string") return d.message;
      if (d.errors) {
        const first = Object.values(d.errors)[0];
        return Array.isArray(first) ? first[0] : String(first);
      }
      return "No se pudo completar la operación.";
    },
    cerrar() {
      this.$emit("close");
    },
    async cargarFirmas() {
      this.cargandoLista = true;
      try {
        const { data } = await axios.get("/api/user/firmas");
        this.firmas = Array.isArray(data) ? data : [];
        const activa = this.firmas.find((f) => f.activa);
        if (activa && this.modoSeleccion) {
          this.seleccionId = activa.id;
        }
      } catch (e) {
        this.$swal({ icon: "error", title: this.mensajeError(e) });
      } finally {
        this.cargandoLista = false;
      }
    },
    onClickFirma(firma) {
      if (this.modoSeleccion) {
        this.seleccionId = firma.id;
      }
    },
    confirmarSeleccion() {
      if (!this.firmaSeleccionada) return;
      this.$emit("selected", this.firmaSeleccionada);
      this.cerrar();
    },
    abrirNueva() {
      this.vista = "nueva";
      this.tab = "dibujo";
      this.alias = "";
      this.previewFoto = null;
      this.archivoFoto = null;
      this.previewExtraida = null;
      this.imagenExtraerOriginal = null;
      this.tieneTrazo = false;
      this.$nextTick(() => {
        this.initCanvas();
        this.limpiarDibujo();
      });
    },
    volverLista() {
      this.vista = "lista";
      this.cargarFirmas();
    },
    cambiarTab(tab) {
      this.tab = tab;
      if (tab === "dibujo") {
        this.$nextTick(() => {
          this.initCanvas();
        });
      }
    },
    initCanvas() {
      const canvas = this.$refs.canvasDibujo;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#111";
    },
    getPos(e) {
      const canvas = this.$refs.canvasDibujo;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    startDraw(e) {
      this.dibujando = true;
      const ctx = this.$refs.canvasDibujo.getContext("2d");
      const p = this.getPos(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    },
    draw(e) {
      if (!this.dibujando) return;
      const ctx = this.$refs.canvasDibujo.getContext("2d");
      const p = this.getPos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      this.tieneTrazo = true;
    },
    endDraw() {
      this.dibujando = false;
    },
    startDrawTouch(e) {
      const touch = e.touches[0];
      this.startDraw({ clientX: touch.clientX, clientY: touch.clientY });
    },
    drawTouch(e) {
      const touch = e.touches[0];
      this.draw({ clientX: touch.clientX, clientY: touch.clientY });
    },
    limpiarDibujo() {
      const canvas = this.$refs.canvasDibujo;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.tieneTrazo = false;
    },
    onFoto(ev) {
      const file = ev.target.files && ev.target.files[0];
      this.archivoFoto = file || null;
      if (!file) {
        this.previewFoto = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.previewFoto = reader.result;
      };
      reader.readAsDataURL(file);
    },
    onExtraer(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) {
        this.imagenExtraerOriginal = null;
        this.previewExtraida = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          this.imagenExtraerOriginal = img;
          this.reprocesarExtraccion();
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    },
    reprocesarExtraccion() {
      if (!this.imagenExtraerOriginal) return;
      const img = this.imagenExtraerOriginal;
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const umbral = this.umbral;
      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = 0;
      let maxY = 0;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const luminancia = 0.299 * r + 0.587 * g + 0.114 * b;
        if (luminancia >= umbral) {
          data[i + 3] = 0;
        } else {
          const px = (i / 4) % canvas.width;
          const py = Math.floor(i / 4 / canvas.width);
          if (px < minX) minX = px;
          if (py < minY) minY = py;
          if (px > maxX) maxX = px;
          if (py > maxY) maxY = py;
        }
      }
      ctx.putImageData(imageData, 0, 0);

      if (maxX <= minX || maxY <= minY) {
        this.previewExtraida = canvas.toDataURL("image/png");
        return;
      }

      const pad = 8;
      const sx = Math.max(0, minX - pad);
      const sy = Math.max(0, minY - pad);
      const sw = Math.min(canvas.width - sx, maxX - minX + pad * 2);
      const sh = Math.min(canvas.height - sy, maxY - minY + pad * 2);
      const cropped = document.createElement("canvas");
      cropped.width = sw;
      cropped.height = sh;
      cropped.getContext("2d").drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
      this.previewExtraida = cropped.toDataURL("image/png");
    },
    async activarFirma(firma) {
      try {
        await axios.put(`/api/user/firmas/${firma.id}/activar`);
        await this.cargarFirmas();
        this.$swal({
          icon: "success",
          title: "Firma activa actualizada",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2500,
        });
      } catch (e) {
        this.$swal({ icon: "error", title: this.mensajeError(e) });
      }
    },
    eliminarFirma(firma) {
      this.$swal({
        title: "¿Eliminar esta firma?",
        text: "No se podrá recuperar.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (!result.value) return;
        try {
          await axios.delete(`/api/user/firmas/${firma.id}`);
          if (this.seleccionId === firma.id) this.seleccionId = null;
          await this.cargarFirmas();
          this.$swal({
            icon: "success",
            title: "Firma eliminada",
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
            timer: 2500,
          });
        } catch (e) {
          this.$swal({ icon: "error", title: this.mensajeError(e) });
        }
      });
    },
    async guardarNueva() {
      if (this.tab === "dibujo" && !this.tieneTrazo) {
        this.$swal({ icon: "warning", title: "Dibuje su firma antes de guardar." });
        return;
      }
      if (this.tab === "foto" && !this.archivoFoto) {
        this.$swal({ icon: "warning", title: "Seleccione una imagen." });
        return;
      }
      if (this.tab === "extraida" && !this.previewExtraida) {
        this.$swal({ icon: "warning", title: "Suba una foto para extraer la firma." });
        return;
      }

      this.guardando = true;
      try {
        const fd = new FormData();
        fd.append("metodo", this.tab);
        if (this.alias) fd.append("alias", this.alias);
        fd.append("activa", "1");

        if (this.tab === "dibujo") {
          fd.append("firma_base64", this.$refs.canvasDibujo.toDataURL("image/png"));
        } else if (this.tab === "foto") {
          fd.append("firma_imagen", this.archivoFoto);
        } else if (this.tab === "extraida") {
          fd.append("firma_base64", this.previewExtraida);
        }

        await axios.post("/api/user/firmas", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.$swal({
          icon: "success",
          title: "Firma guardada",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2500,
        });
        this.$emit("saved");
        this.volverLista();
      } catch (e) {
        this.$swal({ icon: "error", title: this.mensajeError(e) });
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>

<style scoped>
.firma-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.firma-modal {
  background: #fff;
  border-radius: 12px;
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}

.firma-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.firma-modal__body {
  padding: 1.15rem 1.25rem 1.35rem;
}

.firma-tabs .nav-link {
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
}

.firma-pad-wrap {
  text-align: center;
}

.firma-pad {
  width: 100%;
  max-width: 560px;
  height: auto;
  border: 1px dashed #adb5bd;
  border-radius: 8px;
  background: #fff;
  touch-action: none;
  cursor: crosshair;
}

.firma-preview {
  text-align: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}

.firma-preview img {
  max-height: 160px;
  max-width: 100%;
}

.firma-preview--checkered {
  background-image: linear-gradient(45deg, #eee 25%, transparent 25%),
    linear-gradient(-45deg, #eee 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #eee 75%),
    linear-gradient(-45deg, transparent 75%, #eee 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}

.firma-empty {
  text-align: center;
  padding: 1.5rem 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px dashed #ced4da;
}

.firma-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
}

.firma-card {
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #fff;
  padding: 0.65rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.firma-card:hover {
  border-color: #3c8dbc;
}

.firma-card--activa {
  border-color: #28a745;
}

.firma-card--seleccionada {
  border-color: #3c8dbc;
  box-shadow: 0 0 0 2px rgba(60, 141, 188, 0.25);
}

.firma-card__img {
  display: block;
  width: 100%;
  height: 72px;
  object-fit: contain;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 0.4rem;
}

.firma-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
}

.firma-card__alias {
  font-size: 0.8rem;
  font-weight: 600;
  color: #343a40;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.firma-card__actions {
  display: flex;
  gap: 0.35rem;
}

.btn-xs {
  padding: 0.15rem 0.4rem;
  font-size: 0.75rem;
  line-height: 1.2;
}
</style>
